/**
 * Abonnement Prof (migration 0691) : 29,90 €/mois, essai de 7 jours, admins
 * exemptés.
 *
 * - `get_my_tutor_access()` renvoie l'état du compte connecté : `allowed`,
 *   `reason` (admin / subscription / trial / expired / not_tutor), fin de
 *   l'essai et dernier abonnement `tutor_monthly` (Stripe ou store).
 * - `tutorGate` (PUR) décide ce que montre l'espace prof : le contenu, le
 *   paywall ou rien (chargement).
 * - Si le serveur ne répond pas (RPC absente, réseau), l'espace reste OUVERT :
 *   la base reste juge de ce que le prof donne (cartes assignées, exercices,
 *   rendez-vous), une panne ne doit pas enfermer un prof qui paie.
 *
 * RPC postérieure aux types générés → client générique (cf. CLAUDE.md).
 */

import { useQuery } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

const db = supabase as unknown as SupabaseClient;

export const TUTOR_PRICE_LABEL = '29,90 € / mois';
export const TUTOR_TRIAL_DAYS = 7;
export const TUTOR_SUBSCRIPTION_PATH = '/tutor/abonnement';

export type TutorAccessReason =
  | 'admin'
  | 'subscription'
  | 'trial'
  | 'expired'
  | 'not_tutor'
  | 'anonymous'
  | 'no_profile';

export interface TutorSubscriptionInfo {
  provider: 'stripe' | 'revenuecat_ios' | 'revenuecat_android' | string;
  status: string;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
}

export interface TutorAccess {
  allowed: boolean;
  reason: TutorAccessReason;
  trialDays: number;
  trialEndsAt: string | null;
  subscription: TutorSubscriptionInfo | null;
}

type Row = {
  allowed?: boolean | null;
  reason?: string | null;
  trial_days?: number | null;
  trial_ends_at?: string | null;
  subscription?: {
    provider?: string | null;
    status?: string | null;
    current_period_end?: string | null;
    cancel_at_period_end?: boolean | null;
  } | null;
};

/** Réponse de `get_my_tutor_access` → objet de l'app. PUR. */
export function tutorAccessFromRow(row: Row | null | undefined): TutorAccess {
  const sub = row?.subscription ?? null;
  return {
    allowed: !!row?.allowed,
    reason: (row?.reason as TutorAccessReason) ?? 'expired',
    trialDays: row?.trial_days ?? TUTOR_TRIAL_DAYS,
    trialEndsAt: row?.trial_ends_at ?? null,
    subscription: sub
      ? {
          provider: sub.provider ?? 'stripe',
          status: sub.status ?? 'incomplete',
          currentPeriodEnd: sub.current_period_end ?? null,
          cancelAtPeriodEnd: !!sub.cancel_at_period_end,
        }
      : null,
  };
}

/** Jours d'essai restants, arrondis au jour supérieur (0 si terminé). PUR. */
export function trialDaysLeft(trialEndsAt: string | null, now: Date = new Date()): number {
  if (!trialEndsAt) return 0;
  const ms = new Date(trialEndsAt).getTime() - now.getTime();
  return ms > 0 ? Math.ceil(ms / 86_400_000) : 0;
}

/** « 1 jour » / « 3 jours ». */
export function daysLabel(n: number): string {
  return `${n} jour${n > 1 ? 's' : ''}`;
}

/** Date courte FR (« 4 novembre »), année si différente de l'année en cours. */
export function shortDate(iso: string | null, now: Date = new Date()): string {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    ...(d.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}),
  });
}

/** L'abonnement a été acheté dans un store (géré dans les Réglages / Google Play). */
export function isStoreSubscription(sub: TutorSubscriptionInfo | null): boolean {
  return !!sub && sub.provider.startsWith('revenuecat');
}

export type TutorGate = 'loading' | 'open' | 'paywall';

/**
 * Ce que montre l'espace prof. PUR.
 *  - admin (rôle ou e-mail) et tout compte non prof : ouvert (la garde de rôle
 *    est ailleurs) ;
 *  - prof : chargement tant que l'accès est inconnu, puis ouvert (essai,
 *    abonnement, ou serveur injoignable) ou paywall.
 */
export function tutorGate(params: {
  isTutor: boolean;
  isAdmin: boolean;
  loading: boolean;
  failed: boolean;
  access: TutorAccess | undefined;
}): TutorGate {
  if (!params.isTutor || params.isAdmin) return 'open';
  if (params.failed) return 'open';
  if (params.loading || !params.access) return 'loading';
  return params.access.allowed ? 'open' : 'paywall';
}

export const tutorAccessQueryKey = (userId: string | null | undefined) =>
  ['tutor-access', userId ?? null] as const;

/** Accès prof du compte connecté (null = désactivé : admin, élève…). */
export function useTutorAccess(userId: string | null | undefined) {
  return useQuery({
    queryKey: tutorAccessQueryKey(userId),
    enabled: !!userId,
    staleTime: 60_000,
    retry: 1,
    queryFn: async () => {
      const { data, error } = await db.rpc('get_my_tutor_access');
      if (error) throw error;
      return tutorAccessFromRow(data as Row);
    },
  });
}
